import { useState, useMemo } from 'react'

interface UsePaginationProps {
  items: any[]
  itemsPerPage: number
  initialPage?: number
}

export function usePagination({
  items,
  itemsPerPage,
  initialPage = 1
}: UsePaginationProps) {
  const [currentPage, setCurrentPage] = useState(initialPage)

  // Calcular informações da paginação
  const paginationInfo = useMemo(() => {
    const totalItems = items.length
    const totalPages = Math.ceil(totalItems / itemsPerPage)
    
    // Ajustar página atual se necessário
    const adjustedPage = Math.min(Math.max(1, currentPage), totalPages || 1)
    
    // Calcular índices de início e fim
    const startIndex = (adjustedPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    
    // Obter itens da página atual
    const currentItems = items.slice(startIndex, endIndex)
    
    return {
      currentItems,
      currentPage: adjustedPage,
      totalPages,
      totalItems,
      startIndex: startIndex + 1,
      endIndex: Math.min(endIndex, totalItems),
      hasNextPage: adjustedPage < totalPages,
      hasPrevPage: adjustedPage > 1
    }
  }, [items, itemsPerPage, currentPage])

  // Função para mudar de página
  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, page))
  }

  // Função para ir para a próxima página
  const goToNextPage = () => {
    if (paginationInfo.hasNextPage) {
      setCurrentPage(paginationInfo.currentPage + 1)
    }
  }

  // Função para ir para a página anterior
  const goToPrevPage = () => {
    if (paginationInfo.hasPrevPage) {
      setCurrentPage(paginationInfo.currentPage - 1)
    }
  }

  // Função para ir para a primeira página
  const goToFirstPage = () => {
    setCurrentPage(1)
  }

  // Função para ir para a última página
  const goToLastPage = () => {
    setCurrentPage(paginationInfo.totalPages)
  }

  // Resetar para a primeira página quando os itens mudam
  const resetToFirstPage = () => {
    setCurrentPage(1)
  }

  return {
    // Itens da página atual
    currentItems: paginationInfo.currentItems,
    
    // Informações da paginação
    currentPage: paginationInfo.currentPage,
    totalPages: paginationInfo.totalPages,
    totalItems: paginationInfo.totalItems,
    startIndex: paginationInfo.startIndex,
    endIndex: paginationInfo.endIndex,
    
    // Estados de navegação
    hasNextPage: paginationInfo.hasNextPage,
    hasPrevPage: paginationInfo.hasPrevPage,
    
    // Funções de navegação
    goToPage,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
    resetToFirstPage,
    
    // Configuração
    itemsPerPage
  }
} 